import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-ink text-board ring-1 ring-inset ring-ink/10 hover:bg-ink/90 shadow-sm disabled:bg-ink/40',
  secondary:
    'bg-board text-ink border border-line hover:bg-rail hover:border-ink/20 shadow-card',
  outline: 'bg-transparent text-ink border border-line hover:bg-rail',
  ghost: 'bg-transparent text-ink-muted hover:text-ink hover:bg-rail',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-4 text-sm gap-2 rounded-md',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {leadingIcon ? <span className="shrink-0 [&>*]:h-4 [&>*]:w-4">{leadingIcon}</span> : null}
      {children}
      {trailingIcon ? <span className="shrink-0 [&>*]:h-4 [&>*]:w-4">{trailingIcon}</span> : null}
    </button>
  );
});
